import { useEffect } from "react";

const PREFIX = /^\(\d+\+?\)\s/;
const SIZE = 64;

function label(n: number): string {
  return n > 99 ? "99+" : String(n);
}

function iconLink(): HTMLLinkElement | null {
  return document.querySelector<HTMLLinkElement>('link[rel="icon"]');
}

// The page's own icon, kept once so the badge can be drawn on it and removed.
let originalIcon: { href: string; type: string } | null = null;

function restoreIcon() {
  const link = iconLink();
  if (link && originalIcon) {
    link.type = originalIcon.type;
    link.href = originalIcon.href;
  }
}

/** The page icon with a small red dot in its top-right corner, as a PNG
 *  data URL (null when the canvas is unavailable). A number does not read at
 *  tab size (16 px), so the count lives in the title and the icon only says
 *  "something new". */
export function badgeIcon(src: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onerror = () => resolve(null);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = SIZE;
      canvas.height = SIZE;
      const ctx = canvas.getContext("2d");
      if (!ctx) return resolve(null);
      ctx.drawImage(img, 0, 0, SIZE, SIZE);
      const red =
        getComputedStyle(document.documentElement).getPropertyValue("--danger-600").trim() || "#dc2626";
      ctx.beginPath();
      ctx.arc(SIZE - 15, 15, 13, 0, Math.PI * 2);
      ctx.fillStyle = red;
      ctx.fill();
      ctx.lineWidth = 4;
      ctx.strokeStyle = "#ffffff";
      ctx.stroke();
      try {
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.src = src;
  });
}

/** A number in the browser tab, like other mail apps: "(3) Workman — …" in
 *  the title and a red dot on the tab icon. 0 clears both. `alert` (e.g.
 *  "New mail (2)") alternates with that title until it is cleared, to catch
 *  the eye from another tab. Survives other code changing the title (the
 *  prefix is put back). */
export function useTabBadge(count: number, alert?: string | null) {
  const n = Math.max(0, Math.floor(count || 0));
  const unread = n > 0;

  useEffect(() => {
    let base = document.title.replace(PREFIX, "");
    let showAlert = !!alert;
    const badged = () => (n > 0 ? `(${label(n)}) ${base}` : base);
    const render = () => {
      const next = showAlert && alert ? alert : badged();
      if (document.title !== next) document.title = next;
    };
    render();
    const titleEl = document.querySelector("title");
    // Someone else set a new title: keep it as the base, badge it again.
    const obs = titleEl
      ? new MutationObserver(() => {
          if (document.title === alert || document.title === badged()) return;
          base = document.title.replace(PREFIX, "");
          render();
        })
      : null;
    if (titleEl) obs?.observe(titleEl, { childList: true, characterData: true, subtree: true });
    const timer = alert
      ? window.setInterval(() => {
          showAlert = !showAlert;
          render();
        }, 1500)
      : undefined;
    return () => {
      window.clearInterval(timer);
      obs?.disconnect();
      document.title = base;
    };
  }, [n, alert]);

  useEffect(() => {
    const link = iconLink();
    if (!link) return;
    if (!originalIcon) originalIcon = { href: link.href, type: link.type };
    if (!unread) {
      restoreIcon();
      return;
    }
    let cancelled = false;
    void badgeIcon(originalIcon.href).then((url) => {
      // Canvas blocked: the title still shows the number.
      if (cancelled || !url) return;
      link.type = "image/png";
      link.href = url;
    });
    return () => {
      cancelled = true;
    };
  }, [unread]);

  // Signing out / leaving: the plain icon comes back.
  useEffect(() => restoreIcon, []);
}
